// React
import PropTypes from "prop-types";
import { useEffect, useState } from "react";

// React Query
import { useQueryClient } from "@tanstack/react-query";

// Third Party
import moment from "moment-timezone";

// Material-UI
import { Grid, Skeleton, Typography } from "@mui/material";

// Components
import DateTimePickerControlled from "@Layouts/components/DateTimePicker/DateTimePickerControlled.jsx";
import TitleEdit from "@Layouts/components/TitleEdit";
import NotificationActionMotivator from "../NotificationActionMotivator";

// Contexts
import { useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
import { useMotivators } from "@Motivators/context/Common/MotivatorsContext";
import { useSectionMotivators } from "@Motivators/context/Common/MotivatorsSectionContext";

// Custom Hooks
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";

// Helpers
import { and, or } from "@/Common/helpers/sonar";
import { useLocation } from "@/Common/hooks/Location/useLocation";
import { getDateWithTimeZone } from "@/Common/utils/DateUtils";
import { unMaskId } from "@/Motivators/helpers";
import { cleanRowsState, getRowsToBeDeletedAndUpdated, highlightRowsToBeDeleted } from "./HeaderMotivatorHelpers";
import { useCommissionCyclesData } from "@Washouts/hooks/common/useCommissionCyclesData";

// Constants
import { DATE_FORMAT } from "@/Constants/date";

const formatDate = (value) => moment.utc(value).format(DATE_FORMAT.monthAbbrDatePadded);

/**
 * HeaderMotivatorContainer Component - This component is used to render the header of the motivator container. Which includes the title and effective date range.
 * @param props
 * @returns {Element}
 * @constructor
 */
const HeaderMotivatorContainer = (props) => {
	const queryClient = useQueryClient();
	const { useAuth } = useAuthorization();
	const { getSubscription } = useSubscriptions();

	const { motivatorType, identifiers, actions, locationId, isLoading, selection, subscriptions, setIsLoading } = useMotivators();
	const { tableApiRef, tableRows, commissionCycle } = useSectionMotivators();
	const { allCommissionCycles } = useCommissionCyclesData();

	const isEditable = useAuth(actions.editable);

	const selectedLocation = useLocation();

	// States
	const [fromDate, setFromDate] = useState(null);
	const [toDate, setToDate] = useState(null);
	const [showCard, setShowCard] = useState(false);
	const [effectiveDate, setEffectiveDate] = useState({ dataTime: null, type: null });
	const [rowsToUpdate, setRowsToUpdate] = useState([]);
	const [rowsToDelete, setRowsToDelete] = useState([]);
	const [endDateToUpdate, setEndDateToUpdate] = useState();
	const [startDateToUpdate, setStartDateToUpdate] = useState();

	// Props
	const { masterPermission = false, spiffSetup = null } = props;

	const commissionCycleList = queryClient.getQueryData([motivatorType + "Sidebar", locationId]);

	const getIsFinalized = () => {
		if (commissionCycle) {
			return commissionCycle.status === "FINALIZED";
		}
		return false;
	};

	const isFinalized = getIsFinalized();

	const getTitleEditable = () => {
		if (commissionCycle) {
			return commissionCycle.status !== "FINALIZED";
		} else if (selection?.parentCommissionCycleId) {
			const parentCommissionCycle = commissionCycleList?.find((cycle) => cycle._id === selection?.parentCommissionCycleId);
			return parentCommissionCycle?.status !== "FINALIZED";
		} else if (selection?.status) {
			return selection?.status !== "FINALIZED";
		}
		return true;
	};

	// Check if the title is editable depending on the parent commission cycle status
	const isTitleEditable = getTitleEditable();

	/************************************************** SUBSCRIPTIONS **************************************************/
	const id = unMaskId(selection?._id);

	const motivatorsSubscriptions = getSubscription([motivatorType + "Body", locationId, id]);

	// OnUpdateSetup
	motivatorsSubscriptions?.subscriptions?.onUpdateSetup?.setCallback((result) => {
		const motivatorUpdated = result.data?.[subscriptions.get("onUpdateSetup")?.getter];
		setIsLoading(false);
		props.setLoadingDateRange({ from: false, to: false });
		setShowCard(false);

		const allQueries = queryClient.getQueryCache().findAll();
		const cachesToUpdate = allQueries.filter((query) => query.queryHash.includes(motivatorUpdated?._id));

		cachesToUpdate.forEach((cache) => {
			queryClient.setQueryData(cache.queryKey, () => {
				return {
					...motivatorUpdated,
				};
			});
		});
	});

	/************************************************** HELPERS **************************************************/

	const getMinDateFrom = () => {
		const commissionCycleStartDate = moment(commissionCycle?.startDate).utc();
		const minDateFrom =
			motivatorType === "temporaryGuaranteed" || masterPermission
				? commissionCycleStartDate
				: moment.max(commissionCycleStartDate, moment());
		return getDateWithTimeZone(minDateFrom, selectedLocation?.timezone);
	};

	const isInvalid = (from, to, rows) => !from && !to && !rows.length;

	const validateEffectiveDates = ({ apiRef, rowsTable, dateFrom, dateTo, effecDateFromFieldName, effecDateToFieldName, type }) => {
		const allRows = apiRef?.current?.getAllRowIds?.();

		if (isInvalid(dateFrom, dateTo, tableRows)) {
			return true;
		}

		const fromDateValue = moment.utc(dateFrom ?? undefined).format(DATE_FORMAT.monthAbbrDatePadded);
		const toDateValue = moment.utc(dateTo ?? undefined).format(DATE_FORMAT.monthAbbrDatePadded);

		if (allRows?.length) {
			cleanRowsState(apiRef, allRows);
		}

		if (type === "to") {
			setEndDateToUpdate(toDateValue);
		} else {
			setStartDateToUpdate(fromDateValue);
		}

		const { rowsToBeDeleted, rowsToBeUpdated } = getRowsToBeDeletedAndUpdated({
			tableRows: rowsTable,
			fromDate: fromDateValue,
			toDate: toDateValue,
			effecDateFromFieldName,
			effecDateToFieldName,
			type,
		});
		const allRowsToBeTouched = [...rowsToBeDeleted, ...rowsToBeUpdated];

		setRowsToUpdate(rowsToBeUpdated);
		setRowsToDelete(rowsToBeDeleted);

		const flaggedRows = [...rowsToBeDeleted];

		if (flaggedRows?.length === 1 && flaggedRows[0]?.isNew) {
			return false;
		}

		highlightRowsToBeDeleted(apiRef, flaggedRows);

		setShowCard(Boolean(flaggedRows?.length) || Boolean(allRowsToBeTouched?.length));

		if (Boolean(flaggedRows?.length) && Boolean(allRowsToBeTouched?.length)) {
			return false;
		} else {
			return Boolean(!flaggedRows?.length) && Boolean(!allRowsToBeTouched?.length);
		}
	};

	const handleChangeEffectiveDate = (dataTime, type) => {
		const isValid = validateEffectiveDates({
			type,
			apiRef: tableApiRef,
			rowsTable: tableRows,
			dateFrom: type === "from" ? dataTime : fromDate,
			dateTo: type === "to" ? dataTime : toDate,
			effecDateFromFieldName: identifiers.effectiveDateFromFieldName,
			effecDateToFieldName: identifiers.effectiveDateToFieldName,
		});

		setEffectiveDate({ dataTime, type });
		if (type === "from") {
			setFromDate(dataTime);
		} else {
			setToDate(dataTime);
		}

		if (isValid) {
			props.onEditEffectiveDate(dataTime, type);
		}
	};

	const handleDeleteItems = () => {
		const selectedRows = tableApiRef.current.getSelectedRows();
		const selectedIDs = Array.from(selectedRows.keys());

		const idsToDelete = tableRows.filter((row) => selectedIDs.includes(row._id)).map((row) => ({ _id: row._id, operation: "delete" }));
		const idsToUpdate = rowsToUpdate.map((item) => ({ _id: item._id, operation: "update" }));
		const dataToSave = [...idsToDelete, ...idsToUpdate];
		props.onDeleteItems(dataToSave, effectiveDate);
	};

	const isComponentDisabled = () => {
		const effDateFrom = spiffSetup
			? moment(formatDate(spiffSetup?.effectiveDateFrom)).startOf("day")
			: moment(formatDate(selection?.effectiveDateFrom)).startOf("day");
		const today = moment().startOf("day");
		return isLoading || !!selection?.parentCommissionCycleId || effDateFrom.isBefore(today) || !props.hasEditPermission;
	};

	const getIsFromDisabled = () => {
		if (!spiffSetup) {
			return false;
		}
		const effDateFrom = moment(formatDate(spiffSetup?.effectiveDateFrom)).startOf("day");
		const initialCC = allCommissionCycles.find((cc) => effDateFrom.isBetween(moment(cc.startDate), moment(cc.endDate), "[]"));
		if (!initialCC) {
			return false;
		}
		const initialPP = initialCC.payPeriods.find((pp) => effDateFrom.isBetween(moment(pp.startDate), moment(pp.endDate), "[]"));
		if (!initialPP) {
			return false;
		}
		return initialCC.status === "FINALIZED" || initialPP.status === "COMPLETED";
	};
	const isFromDisabled = getIsFromDisabled();

	const handleCancelClick = () => {
		setShowCard(false);
		setFromDate(moment(props.dateRange?.from));
		setToDate(moment(props.dateRange?.to));
		setEndDateToUpdate(null);
		setStartDateToUpdate(null);
		cleanRowsState(tableApiRef, tableApiRef.current.getAllRowIds());
	};

	function renderNotificationActionText() {
		const definitions = [...rowsToUpdate, ...rowsToDelete];
		const question = "Do you wish to continue?";

		if (definitions?.length === 0) {
			return "";
		}

		const actionsToDo = determineActionsToDo(rowsToUpdate.length, rowsToDelete.length);

		const numberOfDefinitions = definitions.length;

		if (or(motivatorType === "spiffSetup", motivatorType === "spiffBonusSetup")) {
			const numberOfAwards = definitions.reduce((acc, def) => {
				const countAwards = def?.awards?.reduce((innerCount, innerAward) => {
					const filteredAwards = innerAward?.awards?.filter((award) => {
						const spiffDate = moment.utc(award.spiffDate).format(DATE_FORMAT.monthAbbrDatePadded);
						const FROM = startDateToUpdate ?? moment(fromDate).format(DATE_FORMAT.monthAbbrDatePadded);
						const END = endDateToUpdate ?? moment(toDate).format(DATE_FORMAT.monthAbbrDatePadded);
						return or(new Date(spiffDate) < new Date(FROM), new Date(award.spiffDate) > new Date(END));
					});
					return innerCount + (filteredAwards?.length ?? 0);
				}, 0);
				return acc + (countAwards ?? 0);
			}, 0);

			const text = [`Changing the Date/Time will ${actionsToDo} definition${numberOfDefinitions > 1 ? "s" : ""}.`];

			if (numberOfAwards > 0) {
				text.push(`This will in turn delete (${numberOfAwards}) associated award${numberOfAwards > 1 ? "s" : ""}.`);
			}

			return `${text.join(" ")} ${question}`;
		} else if (or(motivatorType === "autoAlertsSetup", motivatorType === "temporaryGuaranteed")) {
			return `Changing the Date/Time will adjusted (${numberOfDefinitions}) award${numberOfDefinitions > 1 ? "s" : ""}. ${question}`;
		}

		return "";
	}

	function determineActionsToDo(amountToUpdate, amountToDelete) {
		const arr = [];

		if (amountToUpdate > 0) {
			arr.push(`alter (${amountToUpdate})`);
		}

		if (amountToDelete > 0) {
			arr.push(`delete (${amountToDelete})`);
		}

		return arr.join(" and ");
	}

	const isToDisabled = () => {
		const effectiveDateTo = spiffSetup
			? moment(formatDate(spiffSetup?.effectiveDateTo)).startOf("day")
			: moment(formatDate(selection?.effectiveDateTo)).startOf("day");
		return isLoading || !fromDate || !props.hasEditPermission || moment(effectiveDateTo).isBefore(moment());
	};

	const isTitleNotEditable = () =>
		Boolean(!isEditable || !isTitleEditable || isLoading || !props.hasEditPermission) ||
		moment(selection?.effectiveDateTo).isBefore(moment());

	useEffect(() => {
		if (props.dateRange?.from) {
			setFromDate(moment(props.dateRange?.from));
		}
		if (props.dateRange?.to) {
			setToDate(moment(props.dateRange?.to));
		}

		return () => {
			setFromDate(null);
			setToDate(null);
			setEffectiveDate({ dataTime: null, type: null });
		};
	}, [locationId, props.dateRange]);

	return (
		<Grid container direction="row" justifyContent={"space-between"} alignItems={"center"}>
			<Grid item xs={6} mb={3.5}>
				<TitleEdit
					noEditable={and(isTitleNotEditable(), !masterPermission)}
					title={props.title}
					defaultTitle={props.defaultTitle}
					onEditComplete={props.onEditTitle}
					maxLength={120}
					loading={props?.isLoading || isLoading}
				/>
			</Grid>
			<Grid item xs={6} mb={3.5}>
				<Grid container direction="row" className="justify-end">
					<Typography className="!font-sans text-[14.5px] text-default relative top-3 !ml-[5px] !mr-[4px] font-normal">
						{props.isLoadingData ? <Skeleton width={50} className="!mt-2" /> : "Effective Date from"}
					</Typography>
					<Grid item className="pl-[5px] pr-[5px]">
						<Grid className="items-center flex h-[50px]">
							{or(props?.loadingDateRange?.from, isLoading) ? (
								<Skeleton width={130} height={45} className="relative bottom-0" />
							) : (
								// EFFECTIVE DATE FROM
								<DateTimePickerControlled
									views={["year", "month", "day"]}
									hideTabs
									disabled={and(isComponentDisabled(), !masterPermission) || isFinalized || isFromDisabled}
									disabledInput
									readOnlyInput
									disableHighlightToday
									placement={"bottom-end"}
									sx={{ maxWidth: "130px" }}
									inputFormat={DATE_FORMAT.monthAbbrDatePadded}
									mask={"__/__/____"}
									defaultDate={fromDate}
									onActionUpdate={(dataTime) => handleChangeEffectiveDate(dataTime, "from")}
									minDate={getMinDateFrom()}
									maxDate={toDate}
									error={!fromDate}
									disableContextMenu
									viewRenderers={{
										hours: null,
										minutes: null,
										seconds: null,
									}}
								/>
							)}
						</Grid>
					</Grid>

					<Typography className={"!font-sans text-[14.5px] text-default relative top-3 !ml-[5px] !mr-[4px] font-normal"}>
						{props.isLoadingData ? <Skeleton width={15} className="!mt-2" /> : "to"}
					</Typography>
					<Grid item className="pl-[5px] border-solid border-black border-0">
						<Grid className="items-center flex h-[50px]">
							{or(props.loadingDateRange?.to, isLoading) ? (
								<Skeleton width={130} height={45} sx={{ position: "relative", bottom: "0px" }} />
							) : (
								// EFFECTIVE DATE TO
								<DateTimePickerControlled
									hideTabs
									views={["year", "month", "day"]}
									disabled={and(isToDisabled(), !masterPermission) || isFinalized}
									disableHighlightToday
									placement={"bottom-end"}
									sx={{ maxWidth: "130px" }}
									inputFormat={DATE_FORMAT.monthAbbrDatePadded}
									mask={"__/__/____"}
									defaultDate={toDate}
									onActionUpdate={(dataTime) => handleChangeEffectiveDate(dataTime, "to")}
									minDate={fromDate ?? getMinDateFrom()}
									error={!toDate}
									disableContextMenu
									viewRenderers={{
										hours: null,
										minutes: null,
										seconds: null,
									}}
								/>
							)}
						</Grid>
					</Grid>
				</Grid>
			</Grid>

			{showCard && (
				<NotificationActionMotivator
					notification={{
						isLoading,
						text: renderNotificationActionText(),
						onAccept: handleDeleteItems,
						onCancel: handleCancelClick,
					}}
				/>
			)}
		</Grid>
	);
};

export default HeaderMotivatorContainer;

HeaderMotivatorContainer.propTypes = {
	title: PropTypes.string,
	defaultTitle: PropTypes.string,
	dateRange: PropTypes.shape({
		to: PropTypes.object,
		from: PropTypes.object,
	}),
	loadingDateRange: PropTypes.shape({
		to: PropTypes.bool,
		from: PropTypes.bool,
	}),
	isLoading: PropTypes.bool,
	onEditTitle: PropTypes.func,
	onDeleteItems: PropTypes.func,
	isLoadingData: PropTypes.bool,
	hasEditPermission: PropTypes.bool,
	setLoadingDateRange: PropTypes.func,
	onEditEffectiveDate: PropTypes.func,
	masterPermission: PropTypes.bool,
	spiffSetup: PropTypes.object,
};
